import { useCallback, useLayoutEffect, useState } from "react";

export const OVERFLOW_EPSILON = 2;
export const COLUMN_SCROLL_STEP = 240;
export function canScrollRight({ scrollLeft, clientWidth, scrollWidth }) {
  return scrollLeft + clientWidth < scrollWidth - OVERFLOW_EPSILON;
}

// Callback refs handle loading/view transitions without polling or subscriptions
// to the document. Content observation stays inside the opted-in list.
export function useHorizontalOverflow({ scrollRef, content, invalidate } = {}) {
  const [mounted, setMounted] = useState(null);
  const [geometry, setGeometry] = useState({ canRight: false, remaining: 0, width: 0 });
  const owner = scrollRef?.current || mounted;
  const measure = useCallback(() => {
    if (!owner) return;
    const next = {
      canRight: canScrollRight(owner),
      remaining: Math.max(0, owner.scrollWidth - owner.clientWidth - owner.scrollLeft),
      width: owner.clientWidth,
    };
    setGeometry((prev) => Object.keys(next).every((key) => prev[key] === next[key]) ? prev : next);
  }, [owner]);
  useLayoutEffect(() => {
    if (!owner) return undefined;
    measure();
    const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    resize?.observe(owner);
    if (content && content !== owner) resize?.observe(content);
    const mutation = typeof MutationObserver === "undefined" ? null : new MutationObserver(measure);
    mutation?.observe(content || owner, { childList: true, subtree: true, characterData: true });
    owner.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    return () => {
      resize?.disconnect();
      mutation?.disconnect();
      owner.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, [owner, content, measure]);
  useLayoutEffect(() => { measure(); }, [measure, invalidate]);
  const scrollRight = useCallback(() => {
    owner?.scrollBy({ left: COLUMN_SCROLL_STEP, behavior: "smooth" });
  }, [owner]);
  return { ...geometry, scrollRef: setMounted, scrollRight, measure, owner };
}
